import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { get } from '../api/client.js';
import { useAuth } from '../api/auth.js';
import { Badge, Card, Empty, Failure, Loading } from '../components/ui.js';
import { WEEKDAYS, formatDateTime } from '../lib/format.js';

interface Template {
  id: string;
  name: string;
  notes: string | null;
  shift_count: number;
  shifts: Array<{ id: string; name: string; start_time: string; weekdays: number[]; period_count: number }>;
  assigned_schools: number;
  assigned_groups: number;
  updated_at: string;
}

export function SchedulesPage(): JSX.Element {
  const { can } = useAuth();
  const templates = useQuery({
    queryKey: ['schedules'],
    queryFn: () => get<{ items: Template[]; total: number }>('/schedules?page_size=100'),
  });

  return (
    <Card
      title="قوالب الجداول"
      subtitle={`${templates.data?.total ?? 0} قالب`}
      actions={
        can('schedule.manage') ? (
          <Link className="btn" to="/schedules/new" style={{ background: 'var(--brand)', color: '#fff', borderColor: 'var(--brand)' }}>
            قالب جديد
          </Link>
        ) : null
      }
    >
      {templates.isLoading ? (
        <Loading />
      ) : templates.error ? (
        <Failure error={templates.error} onRetry={() => void templates.refetch()} />
      ) : templates.data!.items.length === 0 ? (
        <Empty
          title="لا توجد قوالب"
          hint="القالب يحدد بداية الدوام ومدد الحصص والاستراحات، ويولّد أحداث الجرس تلقائيًا."
        />
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>القالب</th>
                <th>الدوامات</th>
                <th>الإسناد</th>
                <th>آخر تعديل</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {templates.data!.items.map((t) => (
                <tr key={t.id}>
                  <td>
                    <Link to={`/schedules/${t.id}`}>{t.name}</Link>
                    {t.notes ? (
                      <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>{t.notes}</div>
                    ) : null}
                  </td>
                  <td>
                    <div className="pill-list">
                      {t.shifts.map((s) => (
                        <Badge key={s.id} tone="info" icon="◷">
                          {s.name} <span className="mono">{s.start_time}</span> ·{' '}
                          {s.weekdays
                            .map((w) => WEEKDAYS.find((d) => d.value === w)?.short ?? w)
                            .join('')}{' '}
                          · <span className="num">{s.period_count}</span> فترة
                        </Badge>
                      ))}
                    </div>
                  </td>
                  <td style={{ fontSize: 13 }}>
                    <span className="num">{t.assigned_schools}</span> مدرسة ·{' '}
                    <span className="num">{t.assigned_groups}</span> مجموعة
                  </td>
                  <td style={{ fontSize: 12.5 }}>{formatDateTime(t.updated_at)}</td>
                  <td className="actions">
                    <Link className="btn" to={`/schedules/${t.id}`}>
                      فتح
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
